// Mot de passe oublié : demande d'un code (POST /public/mot-de-passe/demander, réponse identique
// que l'identifiant existe ou non), puis nouveau mot de passe avec le code (/reinitialiser).
// Toutes les sessions ouvertes du compte sont fermées par le serveur.
import { schemaNouveauMotDePasse } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Formulaire, LienBouton } from '@cc/ui';
import { useState } from 'react';
import { requetePublique } from '../../../api/client';
import { ErreurApi, messageErreur } from '../../../api/erreurs';
import { EnTeteCitoyen } from '../commun';
import styles from '../citoyen.module.css';

type ErreurChamp = { champ: string; message: string };

export function PageMotDePasse() {
  const [etape, setEtape] = useState<1 | 2 | 3>(1);
  const [identifiant, setIdentifiant] = useState('');
  const [code, setCode] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreurs, setErreurs] = useState<ErreurChamp[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const erreurChamp = (champ: string) => erreurs.find((e) => e.champ === champ)?.message ?? null;

  const demander = async () => {
    if (!identifiant.trim()) {
      setErreurs([{ champ: 'mdp-identifiant', message: 'Saisissez votre téléphone ou votre courriel' }]);
      return;
    }
    setErreurs([]);
    setErreur(null);
    setEnCours(true);
    try {
      await requetePublique('/public/mot-de-passe/demander', { identifiant: identifiant.trim() });
      setEtape(2);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  const reinitialiser = async () => {
    const locales: ErreurChamp[] = [];
    if (!/^\d{6}$/.test(code.replace(/\s/g, ''))) locales.push({ champ: 'mdp-code', message: 'Le code comporte six chiffres' });
    const r = schemaNouveauMotDePasse.safeParse(motDePasse);
    if (!r.success) locales.push({ champ: 'mdp-nouveau', message: r.error.issues[0]?.message ?? 'Mot de passe trop faible' });
    else if (confirmation !== motDePasse) locales.push({ champ: 'mdp-confirmation', message: 'La confirmation ne correspond pas au mot de passe' });
    setErreurs(locales);
    if (locales.length > 0) return;
    setErreur(null);
    setEnCours(true);
    try {
      await requetePublique('/public/mot-de-passe/reinitialiser', { identifiant: identifiant.trim(), code: code.replace(/\s/g, ''), nouveau_mot_de_passe: motDePasse });
      setEtape(3);
    } catch (e) {
      setErreur(e instanceof ErreurApi && e.statut === 400 ? 'Code incorrect ou expiré. Demandez un nouveau code si besoin.' : messageErreur(e));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <>
      <EnTeteCitoyen titre="Mot de passe oublié" surTitre={etape < 3 ? `Étape ${etape} sur 2` : undefined} retour="/citoyen/connexion" libelleRetour="Connexion" />
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      {etape === 1 ? (
        <Formulaire
          libelle="Demande de code"
          onSoumettre={() => void demander()}
          erreurs={erreurs}
          actions={
            <Bouton type="submit" chargement={enCours} libelleChargement="Envoi en cours" pleineLargeur>
              Recevoir un code
            </Bouton>
          }
        >
          <p>Un code à six chiffres est envoyé par le canal de votre compte (SMS ou courriel). Il est valable quinze minutes.</p>
          <Champ id="mdp-identifiant" libelle="Téléphone ou courriel" autoComplete="username" value={identifiant} onChange={(e) => setIdentifiant(e.target.value)} erreur={erreurChamp('mdp-identifiant')} required />
        </Formulaire>
      ) : etape === 2 ? (
        <Formulaire
          libelle="Nouveau mot de passe"
          onSoumettre={() => void reinitialiser()}
          erreurs={erreurs}
          actions={
            <>
              <Bouton type="submit" chargement={enCours} libelleChargement="Enregistrement en cours" pleineLargeur>
                Changer le mot de passe
              </Bouton>
              <Bouton variante="discret" disabled={enCours} onClick={() => void demander()}>
                Renvoyer le code
              </Bouton>
            </>
          }
        >
          <p>Si ces informations sont valides, un code vient d&apos;être envoyé. Saisissez-le avec votre nouveau mot de passe.</p>
          <Champ id="mdp-code" libelle="Code à six chiffres" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} erreur={erreurChamp('mdp-code')} required />
          <Champ
            id="mdp-nouveau"
            libelle="Nouveau mot de passe"
            type="password"
            autoComplete="new-password"
            aide="Douze caractères au moins, avec une lettre et un chiffre"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            erreur={erreurChamp('mdp-nouveau')}
            required
          />
          <Champ id="mdp-confirmation" libelle="Confirmer le nouveau mot de passe" type="password" autoComplete="new-password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} erreur={erreurChamp('mdp-confirmation')} required />
        </Formulaire>
      ) : (
        <div className={styles.pile}>
          <Bandeau type="succes" titre="Mot de passe changé">
            Vos anciennes sessions sont fermées. Connectez-vous avec le nouveau mot de passe.
          </Bandeau>
          <div>
            <LienBouton href="/citoyen/connexion" variante="principal">
              Se connecter
            </LienBouton>
          </div>
        </div>
      )}
    </>
  );
}
